/**
 * Per-client attendance statuses used on the provider calendar (individual + group).
 * Stored on Appointment.participant extension and QuestionnaireResponse participant-attendance.
 */
import { calendarStatusValues } from '@/schemas/appointment.schema';

export type CalendarClientAttendanceStatus = (typeof calendarStatusValues)[number];

export const CALENDAR_CLIENT_ATTENDANCE_STATUS_VALUES = calendarStatusValues;

/** Matches ProviderCalendarPage CLIENT_STATUS_OPTIONS / TOP_LEVEL_EVENT_STATUS_SELECT_DATA labels. */
export const CALENDAR_CLIENT_ATTENDANCE_OPTIONS: ReadonlyArray<{
  value: CalendarClientAttendanceStatus;
  label: string;
}> = [
  { value: 'proposed', label: 'Requested' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'attended', label: 'Attended' },
  { value: 'clinician-cancelled', label: 'Clinician cancelled' },
  { value: 'client-cancelled', label: 'Client cancelled' },
  { value: 'client-late-cancelled', label: 'Late cancelled' },
  { value: 'client-no-show', label: 'No show' },
] as const;

export const DEFAULT_CALENDAR_CLIENT_ATTENDANCE_STATUS: CalendarClientAttendanceStatus = 'scheduled';

/** Statuses that indicate the client did not attend (strikethrough in headers). */
export const CLIENT_ABSENT_ATTENDANCE_STATUSES = new Set<CalendarClientAttendanceStatus>([
  'client-no-show',
  'client-cancelled',
  'client-late-cancelled',
  'clinician-cancelled',
]);

export function isCalendarClientAttendanceStatus(
  value: string | null | undefined,
): value is CalendarClientAttendanceStatus {
  if (!value) return false;
  return (calendarStatusValues as readonly string[]).includes(value);
}

const LEGACY_ATTENDANCE_MAP: Record<string, CalendarClientAttendanceStatus> = {
  /** Pre-ENG-3443 calendar codes */
  confirmed: 'scheduled',
  completed: 'attended',
  accepted: 'scheduled',
  declined: 'client-no-show',
  tentative: 'proposed',
  'needs-action': 'scheduled',
  'no-show': 'client-no-show',
  noshow: 'client-no-show',
  Attended: 'attended',
  'No-show': 'client-no-show',
  Cancelled: 'client-cancelled',
};

export function normalizeCalendarClientAttendanceStatus(
  raw: string | null | undefined,
): CalendarClientAttendanceStatus | undefined {
  if (!raw) return undefined;
  if (isCalendarClientAttendanceStatus(raw)) return raw;
  const direct = LEGACY_ATTENDANCE_MAP[raw];
  if (direct) return direct;
  const lower = LEGACY_ATTENDANCE_MAP[raw.toLowerCase()];
  if (lower) return lower;
  return undefined;
}

export function getCalendarClientAttendanceLabel(
  status: string | null | undefined,
  fallback = 'Scheduled',
): string {
  const normalized = normalizeCalendarClientAttendanceStatus(status);
  if (!normalized) return status?.trim() || fallback;
  return (
    CALENDAR_CLIENT_ATTENDANCE_OPTIONS.find((o) => o.value === normalized)?.label ?? normalized
  );
}

/** Location is required when the client attended the session. */
export function locationRequiredForClientAttendance(
  status: CalendarClientAttendanceStatus | string | null | undefined,
): boolean {
  const normalized = normalizeCalendarClientAttendanceStatus(status);
  return normalized === 'attended';
}

/** Client attended the session (billable present). */
export function isCalendarClientAttendancePresent(
  status: CalendarClientAttendanceStatus,
): boolean {
  return status === 'attended';
}

export function isCalendarClientAttendanceAbsent(
  status: CalendarClientAttendanceStatus,
): boolean {
  return CLIENT_ABSENT_ATTENDANCE_STATUSES.has(status);
}

/** Terminal attendance for billing/scrubber (attended or documented non-attendance). */
export function isCalendarClientAttendanceResolved(
  status: CalendarClientAttendanceStatus,
): boolean {
  return isCalendarClientAttendancePresent(status) || isCalendarClientAttendanceAbsent(status);
}
